import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { HttpResponse } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { Component, DestroyRef, OnInit, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { concatMap, takeWhile } from 'rxjs/operators';
import { timer } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { Labels } from '../../constants/labels-constant';
import { ValidationMessagesTexts } from '../../constants/validation-messages.constants';
import { Loader } from '../../loader/loader';
import { SupportingDocument } from '../../models/provisioning';
import { ReportPdfType } from '../../models/v2models/report-pdf-type';
import { ReviewSection, isVerifyFlag } from '../../models/v2models/review-state';
import { ProvisioningService } from '../../services/provisioning.service';
import {
  ExportFormat,
  ReviewStateService,
} from '../../services/review-state.service';
import { ReportV2Details, ReportV2Service } from '../../services/report-v2.service';
import { SnackbarService } from '../../services/snackbar.service';
import { ReportStatus } from '../../models/report';

/**
 * Screen 05 · Compile & Export.
 *
 * Orders the updated report body and its appendices for final assembly (US-17-01), naming each
 * divider from the document's title (US-17-02). The report body is always first and cannot be
 * moved — only the appendices reorder around it.
 */
@Component({
  selector: 'app-compile-export',
  standalone: true,
  imports: [CommonModule, DragDropModule, MatIconModule, Loader],
  templateUrl: './compile-export.html',
  styleUrl: './compile-export.scss',
})
export class CompileExport implements OnInit {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private provisioningService = inject(ProvisioningService);
  private reviewStateService = inject(ReviewStateService);
  private reportV2Service = inject(ReportV2Service);
  private snackbar = inject(SnackbarService);
  private destroyRef = inject(DestroyRef);

  /** Generation is server-side and slow, so progress is polled rather than awaited. */
  /** Export URL availability is polled until the backend publishes the file location. */
  private static readonly EXPORT_POLL_INTERVAL_MS = 5_000;
  /** Temporary local testing toggle: bypass quick-preview API + polling and open viewer directly. */

  labels = Labels;

  reportId = 0;
  isLoading = false;
  isSaving = false;

  readonly header = signal<ReportV2Details | null>(null);
  readonly documents = signal<SupportingDocument[]>([]);
  readonly isLoaded = signal(false);

  /** Set once the list has been dragged away from the order the server returned. */
  readonly isDirty = signal(false);

  readonly hasDocuments = computed(() => this.documents().length > 0);

  /** Which export is in flight, so only that button shows a busy state. */
  readonly exporting = signal<ExportFormat | null>(null);
  readonly downloading = signal<ExportFormat | null>(null);
  readonly isExportBusy = computed(() => this.exporting() !== null || this.downloading() !== null || this.isPreviewing());
  readonly isPreviewing = signal(false);

  /** Review candidates, loaded so pending is counted the same way the review screen counts it. */
  readonly reviewSections = signal<ReviewSection[]>([]);

  /** "6 of 8 confirmed" in the header, from the report's decision counters. */
  readonly decisions = computed(() => {
    const header = this.header();
    const accepted = header?.accepted ?? 0;
    const declined = header?.declined ?? 0;
    // Verify flags sit in PROPOSED forever and are never decidable, so exclude them the way the
    // review screen does — the server's raw PROPOSED count includes them and reads too high.
    const pending = this.reviewSections()
      .flatMap((section) => section.changes)
      .filter((change) => !isVerifyFlag(change) && change.status === 'Pending').length;
    return { accepted, total: accepted + declined + pending, pending };
  });

  ngOnInit(): void {
    this.route.queryParams
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
        this.reportId = Number(params['reportId']) || 0;
        this.loadHeader();
        this.loadReviewSections();
        this.loadDocuments();
      });
  }

  private loadReviewSections(): void {
    this.reviewStateService.getReviewSections(this.reportId).subscribe({
      next: (sections) => this.reviewSections.set(sections),
      // Pending is a warning only; a failure here must not block ordering or export.
      error: () => this.reviewSections.set([]),
    });
  }

  private loadHeader(): void {
    this.reportV2Service.getReportDetails(this.reportId).subscribe({
      next: (response) => {
        if (response.success) {
          this.header.set(response.data);
          if (response.data?.status === ReportStatus.Review) {
            this.updateReportStatus(this.reportId);
          }
        }
      },
      // Supporting detail only — a failure here must not block the ordering list.
      error: () => this.header.set(null),
    });
  }

  private updateReportStatus(reportId: number) {
      this.reportV2Service.updateReportStatus(reportId, { status: ReportStatus.Download }).subscribe({
        next: (response) => {
          if (response.success) {
            return;
          }
  
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: response.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG,
          });
        },
        error: (err) => {
          const errorMessage =
            err?.error?.message || ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG;
          this.snackbar.showCustom({
            type: 'error',
            title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
            message: errorMessage,
          });
        },
      });
    }

  private loadDocuments(): void {
    this.isLoading = true;
    this.provisioningService.getSupportingDocuments(this.reportId).subscribe({
      next: (documents) => {
        this.documents.set(documents);
        this.isDirty.set(false);
        this.isLoaded.set(true);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
        this.isLoaded.set(true);
        this.snackbar.showCustom({
          type: 'error',
          title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
          message: Labels.COMPILE_EXPORT.TOASTER.LOAD_FAILED,
        });
      },
    });
  }

  // ---------------------------------------------------------------- ordering

  // Rearranging only touches local state — nothing is sent, and nothing toasts, until the
  // reviewer clicks the Accept order button. Client feedback: a toast per drag step was noise.
  onDrop(event: CdkDragDrop<SupportingDocument[]>): void {
    if (event.previousIndex === event.currentIndex) return;
    this.documents.update((documents) => {
      const reordered = [...documents];
      moveItemInArray(reordered, event.previousIndex, event.currentIndex);
      return reordered;
    });
    this.isDirty.set(true);
    this.saveOrder();
  }

  /** Keyboard/click equivalent of dragging, so ordering does not depend on a pointer. */
  move(index: number, offset: number): void {
    const target = index + offset;
    if (target < 0 || target >= this.documents().length) return;
    this.documents.update((documents) => {
      const reordered = [...documents];
      moveItemInArray(reordered, index, target);
      return reordered;
    });
    this.isDirty.set(true);
    this.saveOrder();
  }

  /** Persists the current order — the single point where the sequence API is called. */
  saveOrder(): void {
    // Sequence is 1-based and assigned from the list order, so it is always contiguous even if
    // the documents arrived unsequenced.
    const items = this.documents().map((document, index) => ({
      fileId: document.fileId,
      sequence: index + 1,
    }));

    this.isSaving = true;
    this.provisioningService.updateSupportingDocumentSequence(this.reportId, items).subscribe({
      next: () => {
        this.isSaving = false;
        this.isDirty.set(false);
        // Keep the local copy in step so a later save sends the same order the server holds.
        this.documents.update((documents) =>
          documents.map((document, index) => ({ ...document, sequence: index + 1 })),
        );
      },
      error: () => {
        this.isSaving = false;
      },
    });
  }

  // ---------------------------------------------------------------- helpers

  /** The divider name for an item, falling back to the file name when untitled. */
  dividerName(document: SupportingDocument): string {
    return document.title?.trim() || document.originalName;
  }

  fileKind(document: SupportingDocument): string {
    const extension = document.originalName.split('.').pop();
    return extension ? extension.toUpperCase() : '';
  }

  // ---------------------------------------------------------------- preview

  /**
   * Starts preview generation with appendices included, then opens the existing PDF viewer.
   */
  previewReport(): void {
    this.isPreviewing.set(true);
    this.reviewStateService.generateQuickPreview(this.reportId, true).subscribe({
      next: () => this.openPreview(),
      error: () => this.failPreview(Labels.COMPILE_EXPORT.TOASTER.PREVIEW_FAILED),
    });
  }

  private openPreview(): void {
    this.isPreviewing.set(false);
    // Reuses the app's existing PDF viewer route, same as the report list's preview.
    this.router.navigate(['/preview-report'], {
      queryParams: {
        reportId: this.reportId,
        type: ReportPdfType.Preview,
        reportname: this.header()?.reportName ?? '',
        hideDownload: 'true',
        directPreview: 'true',
      },
      state: { fromPdfViewer: true, returnUrl: this.router.url },
    });
  }

  private failPreview(message: string, type: 'error' | 'warning' = 'error'): void {
    this.isPreviewing.set(false);
    this.snackbar.showCustom({
      type,
      title:
        type === 'warning'
          ? ValidationMessagesTexts.TOASTER.WARNING.TITLE
          : ValidationMessagesTexts.TOASTER.ERROR.TITLE,
      message,
    });
  }

  // ---------------------------------------------------------------- export

  generateConsolidatedPdf(): void {
    this.export('pdf');
  }

  downloadUpdatedDocx(): void {
    this.export('docx');
  }

  private export(format: ExportFormat): void {
    this.exporting.set(format);
    this.reviewStateService.exportReport(this.reportId, format).subscribe({
      next: (result) => {
        if (result.success) {
          this.pollExportFileUrl(format, result.reportId);
          return;
        }

        this.exporting.set(null);
        this.showExportFailed(ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG);
      },
      error: () => {
        this.exporting.set(null);
        this.showExportFailed(ValidationMessagesTexts.TOASTER.ERROR.SOMETHING_WENT_WRONG);
      },
    });
  }

  private pollExportFileUrl(format: ExportFormat, reportId: number = this.reportId): void {
    timer(0, CompileExport.EXPORT_POLL_INTERVAL_MS)
      .pipe(
        // Keep each request in order; a slower response should not be replaced by a newer tick.
        concatMap(() => this.reportV2Service.getReportDetails(reportId)),
        takeWhile(
          (response) =>
            response.success &&
            !(format === 'pdf' ? response.data.pdfFileUrl : response.data.docFileUrl),
          true,
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: (response) => {
          if (!response.success) {
            this.exporting.set(null);
            this.showExportFailed();
            return;
          }

          this.header.set(response.data);
          const fileUrl =
            format === 'pdf' ? response.data.pdfFileUrl : response.data.docFileUrl;

          if (!fileUrl) {
            return;
          }

          this.exporting.set(null);
          this.downloadGeneratedFile(format);
        },
        error: () => {
          this.exporting.set(null);
          this.showExportFailed();
        },
      });
  }


  private downloadGeneratedFile(format: ExportFormat): void {
    this.downloading.set(format);

    const type = format === 'pdf' ? ReportPdfType.FinalReport : ReportPdfType.FinalWordDocument;
    this.reportV2Service.getReportPdfStream(this.reportId, type).subscribe({
      next: (response) => this.downloadFile(response, format),
      error: () => {
        this.downloading.set(null);
        this.showExportFailed();
      },
    });
  }

  private downloadFile(response: HttpResponse<Blob>, format: ExportFormat): void {
    const fileBlob = response.body;
    if (!fileBlob) {
      this.downloading.set(null);
      this.showExportFailed();
      return;
    }

    const contentType = response.headers.get('content-type') || this.defaultContentType(format);
    const fileName = this.getDownloadFileName(response, format);
    const fileUrl = URL.createObjectURL(new Blob([fileBlob], { type: contentType }));

    // Let Angular render the "Downloading..." state before triggering the browser download.
    requestAnimationFrame(() => {
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = fileName;
      link.rel = 'noopener';
      link.click();
      URL.revokeObjectURL(fileUrl);
      this.downloading.set(null);
    });
  }

  private getDownloadFileName(response: HttpResponse<Blob>, format: ExportFormat): string {
    const contentDisposition = response.headers.get('content-disposition');
    const headerFileName = this.parseContentDispositionFileName(contentDisposition);
    return headerFileName || `${this.exportFileName()}.${format}`;
  }

  private parseContentDispositionFileName(contentDisposition: string | null): string | null {
    if (!contentDisposition) {
      return null;
    }

    const encodedMatch = /filename\*=UTF-8''([^;]+)/i.exec(contentDisposition);
    if (encodedMatch?.[1]) {
      return decodeURIComponent(encodedMatch[1].trim().replace(/^"|"$/g, ''));
    }

    const plainMatch = /filename=([^;]+)/i.exec(contentDisposition);
    return plainMatch?.[1]?.trim().replace(/^"|"$/g, '') || null;
  }

  private defaultContentType(format: ExportFormat): string {
    return format === 'pdf'
      ? 'application/pdf'
      : 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
  }

  private showExportFailed(message?: string): void {
    this.snackbar.showCustom({
      type: 'error',
      title: ValidationMessagesTexts.TOASTER.ERROR.TITLE,
      message: message || Labels.COMPILE_EXPORT.TOASTER.EXPORT_FAILED,
    });
  }

  private exportFileName(): string {
    const name = this.header()?.reportName?.trim() || `report-${this.reportId}`;
    return name.replace(/[\/:*?"<>|]+/g, '-');
  }

  // ---------------------------------------------------------------- helpers

  backToReview(): void {
    this.router.navigate(['/report-review'], { queryParams: { reportId: this.reportId } });
  }

  close(): void {
    this.router.navigate(['/reports']);
  }
}
